// Identity + text + SVG tile engine (see WATERMARK_PORTABLE_BUILD_GUIDE §4.1). Framework-agnostic.
import type { WatermarkConfig } from './watermark-config';

export interface WatermarkTokens {
  bankName: string;
  userName: string;
  employeeId: string;
  userEmail: string;
  sessionId?: string;
  ipAddress?: string;
}

const SID_KEY = 'app_wm_sid';
/** Stable-per-tab session id (survives reloads). Never leaves the client except inside the mark. */
export function watermarkSessionId(): string {
  try {
    let v = sessionStorage.getItem(SID_KEY);
    if (!v) { v = crypto.randomUUID(); sessionStorage.setItem(SID_KEY, v); }
    return v;
  } catch { return 'nosession'; }
}

/** djb2 → base36. Forensic fingerprint — attributes a cropped fragment. */
export function shortHash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

const fmtDate = (d: Date) => d.toLocaleDateString();
const fmtDateTime = (d: Date) => d.toLocaleString();

/** Compose the repeated string from selected parts + live values. 'timestamp' is computed at
 *  call time, so re-resolving refreshes it (used by rotation). */
export function resolveWatermarkText(cfg: WatermarkConfig, t: WatermarkTokens): string {
  const map: Record<string, string> = {
    bankName: t.bankName, userName: t.userName, employeeId: t.employeeId, userEmail: t.userEmail,
    date: fmtDate(new Date()),
    sessionId: t.sessionId ? t.sessionId.slice(0, 8) : '',
    timestamp: fmtDateTime(new Date()),
    ipAddress: t.ipAddress ?? '',
    custom: cfg.customText.trim(),
  };
  return cfg.parts.map((p) => map[p] ?? '').filter(Boolean).join(cfg.separator);
}

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

/** FNV-1a → seeded mulberry32 PRNG. Same seed → same sequence (scatter stable per session). */
function seededRng(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) { h ^= seed.charCodeAt(i); h = Math.imul(h, 16777619); }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function textNode(text: string, cfg: WatermarkConfig, x: number, y: number, rot: number): string {
  return `<text x='${x}' y='${y}' fill='${cfg.color}' fill-opacity='${cfg.opacity}' ` +
    `font-family='system-ui, sans-serif' font-size='${cfg.fontSize}' font-weight='${cfg.fontWeight}' ` +
    `text-anchor='middle' dominant-baseline='middle' ` +
    `transform='rotate(${rot} ${x} ${y})'>${escapeXml(text)}</text>`;
}

/** Repeating SVG tile as a background-image data URI (one element, no per-tile DOM). With
 *  cfg.scatter, a 2×2 super-cell holds one jittered instance per cell (seeded from `seed`). */
export function buildTileUrl(text: string, cfg: WatermarkConfig, seed?: string): string {
  // Size the tile to the ROTATED text's bounding box, then add `gap` for spacing.
  // Sizing to the un-rotated width (as in the reference guide) clips the text once
  // the rotation makes it taller/wider than the tile — most visible at low density.
  const rad = (cfg.rotation * Math.PI) / 180;
  const tw = text.length * cfg.fontSize * 0.62; // approx text width
  const th = cfg.fontSize * 1.35;               // approx text line height
  const bw = Math.abs(tw * Math.cos(rad)) + Math.abs(th * Math.sin(rad));
  const bh = Math.abs(tw * Math.sin(rad)) + Math.abs(th * Math.cos(rad));
  const cw = Math.ceil(bw) + cfg.gap;
  const ch = Math.ceil(bh) + cfg.gap;
  if (!cfg.scatter) {
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${cw}' height='${ch}'>` +
      textNode(text, cfg, cw / 2, ch / 2, cfg.rotation) + `</svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
  }
  const W = cw * 2, H = ch * 2, r = seededRng(seed || text);
  const nodes = [[0, 0], [1, 0], [0, 1], [1, 1]].map(([cx, cy]) => {
    const x = cx * cw + cw / 2 + (r() - 0.5) * cw * 0.5;
    const y = cy * ch + ch / 2 + (r() - 0.5) * ch * 0.5;
    return textNode(text, cfg, x, y, cfg.rotation + (r() - 0.5) * 16);
  }).join('');
  return `url("data:image/svg+xml,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' width='${W}' height='${H}'>${nodes}</svg>`)}")`;
}
